import React, { useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useFormState, Field } from "react-final-form";
import { motion } from "framer-motion";
import dayjs from "dayjs";

import Fade from "../animate/Fade";
import EstimateLoader from "./EstimateLoader";
import { getEstimate } from "../actions";

const advancedFormat = require("dayjs/plugin/advancedFormat");

dayjs.extend(advancedFormat);

const Summary = ({
  fetchEstimate,
  loading,
  price,
  to,
  from,
  packingCost,
  error,
  previousValues,
  dateRange,
}) => {
  const { values } = useFormState();

  const {
    live_in,
    move_size,
    moving_from,
    moving_to,
    international,
    move_date,
    date_flexible,
    firstname,
    lastname,
    email,
    phone,
    coupon,
    repeat_client,
    add_packing,
    paymenttoken,
    estimateActions,
  } = values;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utm_source = urlParams.get("utm_source");

    const newValues = {
      live_in,
      move_size,
      moving_from,
      moving_to,
      international,
      move_date: dayjs(move_date).toISOString(),
      date_flexible,
      firstname,
      lastname,
      email,
      phone,
      coupon,
      repeat_client,
      utm_source,
      move_info: utm_source,
    };

    if (
      JSON.stringify(newValues) !== JSON.stringify(previousValues) ||
      !price
    ) {
      fetchEstimate(newValues);
    }
  }, [
    live_in,
    move_size,
    moving_from,
    moving_to,
    international,
    move_date,
    date_flexible,
    firstname,
    lastname,
    email,
    phone,
    coupon,
    repeat_client,
  ]);

  if (error) {
    return null;
  }

  // Instead of showing the price directly, we want to:
  // 1. Round it up to the nearest hundred
  // 2. Expose a range that is -300 and +300 of the rounded price
  // Unless the price is less than 300, in which case we just show +300

  const estimatedPrice =
    Math.ceil((price + (add_packing ? packingCost : 0)) / 100) * 100;

  const priceRange = {
    min: estimatedPrice < 300 ? estimatedPrice : estimatedPrice - 300,
    max: estimatedPrice + 300,
  };

  const printablePriceRange = `$${priceRange.min
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")} - $${priceRange.max
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

  return loading && estimateActions !== "contact" ? (
    <EstimateLoader color="#000" position="relative" />
  ) : (
    <Fade show>
      <motion.div className="quote-summary" layout>
        {(() => {
          return (
            <Fade show>
              <h4>Summary:</h4>
              <p>
                {(() => {
                  switch (move_size) {
                    case "2BR":
                      return "Two bedroom";
                    case "3BR":
                      return "Three bedroom";
                    case "4BR":
                      return "Four+ bedroom";
                    default:
                      return "A few items from a";
                  }
                })()}{" "}
                {move_size === "Few" && live_in === "apartment" ? "n" : ""}
                {live_in === "other" ? "residence" : live_in}. Moving from{" "}
                <span className="bold">
                  {(from && from.city) || ""} ({(from && from.zip) || ""})
                </span>{" "}
                to{" "}
                <span className="bold">
                  {(to && to.city) || ""} ({(to && to.zip) || ""})
                </span>{" "}
                {date_flexible
                  ? ` between ${dayjs(dateRange.startDate).format(
                      "dddd, [the] Do [of] MMMM"
                    )} and ${dayjs(dateRange.endDate).format(
                      "dddd, [the] Do [of] MMMM"
                    )}`
                  : ` on ${dayjs(move_date).format(
                      "dddd, [the] Do [of] MMMM"
                    )}. `}
              </p>
              <div className="quote-summary__row">
                <h4>Your estimate: </h4>
                <h3>{printablePriceRange}</h3>
              </div>
              <div className="radio-toolbar">
                <Fade show={!!price}>
                  <label className="checkbox-wrap" htmlFor="add_packing">
                    <Field
                      id="add_packing"
                      name="add_packing"
                      component="input"
                      type="checkbox"
                      value="true"
                      className="checkbox"
                      parse={(v) => !!v[0]}
                      format={(v) => [v ? "true" : "false"]}
                      disabled={!!paymenttoken}
                    />{" "}
                    Include packing?
                  </label>
                </Fade>
              </div>
            </Fade>
          );
        })()}
      </motion.div>
    </Fade>
  );
};

Summary.propTypes = {
  fetchEstimate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  price: PropTypes.number,
  to: PropTypes.shape({ city: PropTypes.string, zip: PropTypes.string }),
  from: PropTypes.shape({ city: PropTypes.string, zip: PropTypes.string }),
  packingCost: PropTypes.number,
  error: PropTypes.instanceOf(Error),
  // eslint-disable-next-line react/forbid-prop-types
  previousValues: PropTypes.object,
  dateRange: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }),
};

Summary.defaultProps = {
  loading: true,
  price: 0,
  to: null,
  from: null,
  packingCost: 0,
  error: null,
  previousValues: {},
  dateRange: null,
};

const mapDispatchToProps = (dispatch) => ({
  fetchEstimate: (values) => dispatch(getEstimate(values)),
});

const mapStateToProps = (state) => ({
  loading: state.estimate.loading,
  price: state.estimate.estimate,
  to: state.estimate.to,
  from: state.estimate.from,
  packingCost: state.estimate.packing_cost,
  error: state.estimate.error,
  previousValues: state.estimate.previousValues,
  dateRange: state.estimate.date_range,
});

export default connect(mapStateToProps, mapDispatchToProps)(Summary);
